import { apiGet, apiPost, apiPut, buildUrl, readReason } from '@/api/client';
import type { DepartmentMember } from '@/api/types';
import { errorForStatus, NetworkError } from '@/api/errors';
import { getAccessToken } from '@/features/auth/token';
import type { EvaluationPhase, Pilot, PilotEvaluation, PilotStatus } from '@/features/pilots/types';

/**
 * `/pilots/{id}/evaluations` (GET) and `/pilots/{id}/statistics` are real,
 * deployed endpoints (`php/api_pilots.php`), but too new to be in the
 * generated `src/api/schema.ts` yet (that file only reflects the last
 * `generate:api` run against the deployed OpenAPI doc) — same rationale
 * `src/features/challenges/api.ts` documents for its own `request()`. Once a
 * `generate:api` run picks these up, these two call sites can switch to
 * `apiGet`.
 */
async function request<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = buildUrl(path, params);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, { headers });
  } catch (cause) {
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(response.status, `GET ${path} failed`, await readReason(response));
  }

  return (await response.json()) as T;
}

/**
 * A pilot "counts" for the Users page's Pilot column and unlink action once
 * it has actually started and until it is fully scaled out — 'draft' (not
 * yet running) and 'scaled' (graduated into a normal rollout, no longer a
 * pilot) are excluded. There is no single source-of-truth flag for this in
 * the data model, so the rule lives here.
 */
const ONGOING_PILOT_STATUSES: PilotStatus[] = [
  'baseline',
  'active',
  'interim_review',
  'final_review',
  'completed',
];

export interface PilotMembership {
  pilotId: string;
  pilotName: string;
  departmentId: string;
}

/**
 * Which of a company's users currently sit in an ongoing pilot's department,
 * and through which pilot/department — for the Users page's Pilot column
 * (Ja/–) and its "Ontkoppelen van pilot" row action (WP-01 ×  WP-04). Since
 * `pilots`/`pilot_departments` link by department, not by user,
 * "unlinking" one person removes their whole department from the pilot —
 * documented on the action itself, not hidden.
 */
export async function fetchPilotMembership(
  companyId: string,
): Promise<Map<string, PilotMembership>> {
  const pilots = await listPilots(companyId);
  const ongoing = pilots.filter((p) => ONGOING_PILOT_STATUSES.includes(p.status));

  const map = new Map<string, PilotMembership>();
  for (const pilot of ongoing) {
    for (const departmentId of pilot.department_ids) {
      let members: DepartmentMember[];
      try {
        members = (await apiGet('/departments/{departmentId}/users', {
          params: { departmentId },
        })) as unknown as DepartmentMember[];
      } catch {
        continue;
      }
      for (const member of members) {
        map.set(member.user_id, { pilotId: pilot.id, pilotName: pilot.name, departmentId });
      }
    }
  }

  return map;
}

/** Removes one department from a pilot's pilotgroep — the "Ontkoppelen van
 * pilot" row action on the Users page. Reads the pilot first so the
 * full-replace `PUT /pilots/{id}` body carries every other department
 * unchanged. */
export async function unlinkDepartmentFromPilot(
  pilotId: string,
  departmentId: string,
): Promise<void> {
  const pilot = await getPilot(pilotId);
  await updatePilot(pilotId, {
    department_ids: pilot.department_ids.filter((id) => id !== departmentId),
  });
}

export function listPilots(companyId: string): Promise<Pilot[]> {
  return apiGet('/pilots', { query: { company_id: companyId } }) as unknown as Promise<Pilot[]>;
}

export function getPilot(pilotId: string): Promise<Pilot> {
  return apiGet('/pilots/{pilotId}', { params: { pilotId } }) as unknown as Promise<Pilot>;
}

export function createPilot(input: {
  company_id: string;
  name: string;
  department_ids: string[];
  start_date?: string | null;
}): Promise<{ pilot_id: string }> {
  return apiPost('/pilots', { body: input }) as unknown as Promise<{ pilot_id: string }>;
}

export function updatePilot(
  pilotId: string,
  input: {
    name?: string;
    start_date?: string | null;
    end_date?: string | null;
    department_ids?: string[];
  },
): Promise<void> {
  return apiPut('/pilots/{pilotId}', {
    params: { pilotId },
    body: input,
  }) as unknown as Promise<void>;
}

export function setPilotStatus(pilotId: string, status: PilotStatus): Promise<void> {
  return apiPut('/pilots/{pilotId}/status', {
    params: { pilotId },
    body: { status },
  }) as unknown as Promise<void>;
}

export function addPilotEvaluation(
  pilotId: string,
  input: {
    phase: EvaluationPhase;
    conclusions?: string | null;
    satisfaction_score?: number | null;
  },
): Promise<{ evaluation_id: string }> {
  return apiPost('/pilots/{pilotId}/evaluations', {
    params: { pilotId },
    body: input,
  }) as unknown as Promise<{ evaluation_id: string }>;
}

export function setPilotRecommendation(pilotId: string, recommendation: string): Promise<void> {
  return apiPut('/pilots/{pilotId}/recommendation', {
    params: { pilotId },
    body: { recommendation },
  }) as unknown as Promise<void>;
}

/** Every evaluation recorded for a pilot, oldest first — hydrates the
 * Conclusies list on page load (`PilotDetailPage`'s "Aanbevelingen" half is
 * still client-only, since `PilotEvaluation` itself has no field for it). */
export function getPilotEvaluations(pilotId: string): Promise<PilotEvaluation[]> {
  return request<PilotEvaluation[]>(`/pilots/${pilotId}/evaluations`);
}

const KPI_IDS = [
  'participationRate',
  'activeUsers',
  'dailyUsage',
  'satisfaction',
  'avgVitality',
  'aiCoachUsage',
  'challengeUsage',
  'rolloutReadiness',
] as const;

export type PilotKpiId = (typeof KPI_IDS)[number];

/** One of a pilot's 8 KPI cards. `status: 'unavailable'` covers both "no
 * statistics endpoint yet" (WP-03) and "insufficient data" alike — the card
 * shows the same neutral "nog geen data" regardless of which. */
export interface PilotKpi {
  id: PilotKpiId;
  value: number | null;
  target: number | null;
  unit: 'percent' | 'count' | 'score';
  status: 'ok' | 'unavailable';
}

const BASELINE_DELTA_IDS = ['stress', 'sleep'] as const;
export type PilotBaselineDeltaId = (typeof BASELINE_DELTA_IDS)[number];

/** The hero banner's two extra tiles (Stress/Slaap, "vs nulmeting") — a
 * change vs. the baseline measurement rather than a value-against-target,
 * so it's kept separate from `PilotKpi`. `stress` is percentage points
 * (negative = calmer), `sleep` is minutes/day (positive = more sleep). */
export interface PilotBaselineDelta {
  id: PilotBaselineDeltaId;
  value: number | null;
  status: 'ok' | 'unavailable';
}

export interface PilotStatistics {
  kpis: PilotKpi[];
  baselineDeltas: PilotBaselineDelta[];
}

interface PilotStatisticsResponse {
  status: 'ok' | 'insufficient_data';
  kpis: Record<PilotKpiId, { value: number | null; target: number | null }>;
  baseline_deltas: { stress_change_pct: number | null; sleep_change_minutes: number | null };
}

/**
 * The pilot detail page's statistics — the 8 KPI cards plus the hero's
 * Stress/Slaap deltas, fed by `GET /pilots/{id}/statistics`
 * (`php/api_pilots.php`'s `statistics()`). `status: 'insufficient_data'`
 * (below the same minimum-group-size threshold `ParticipationCalculator`
 * uses elsewhere) and a missing/failed request both fold into this page's
 * own `status: 'unavailable'` per card/tile — the card shows the same
 * neutral "no data" regardless of which.
 */
export async function fetchPilotStatistics(pilotId: string): Promise<PilotStatistics> {
  const unavailableKpis: PilotKpi[] = KPI_IDS.map((id) => ({
    id,
    value: null,
    target: null,
    unit: id === 'satisfaction' || id === 'avgVitality' ? 'score' : 'percent',
    status: 'unavailable',
  }));
  const unavailableDeltas: PilotBaselineDelta[] = BASELINE_DELTA_IDS.map((id) => ({
    id,
    value: null,
    status: 'unavailable',
  }));
  const unavailable: PilotStatistics = { kpis: unavailableKpis, baselineDeltas: unavailableDeltas };

  try {
    const body = await request<PilotStatisticsResponse>(`/pilots/${pilotId}/statistics`);
    const overallOk = body.status === 'ok';

    const kpis: PilotKpi[] = KPI_IDS.map((id) => {
      const entry = body.kpis?.[id];
      const value = entry?.value ?? null;
      return {
        id,
        value,
        target: entry?.target ?? null,
        unit: id === 'satisfaction' || id === 'avgVitality' ? 'score' : 'percent',
        status: overallOk && value !== null ? 'ok' : 'unavailable',
      };
    });

    const baselineDeltas: PilotBaselineDelta[] = BASELINE_DELTA_IDS.map((id) => {
      const value =
        id === 'stress'
          ? (body.baseline_deltas?.stress_change_pct ?? null)
          : (body.baseline_deltas?.sleep_change_minutes ?? null);
      return { id, value, status: overallOk && value !== null ? 'ok' : 'unavailable' };
    });

    return { kpis, baselineDeltas };
  } catch {
    return unavailable;
  }
}

/**
 * A real (not invented) headcount for the pilot info card's "N deelnemers" —
 * the sum of each pilot department's member list, same per-department
 * `GET /departments/{id}/users` call `fetchPilotMembership` already makes,
 * with the same defensive per-department try/catch (one department the
 * caller can't read shouldn't zero out the whole count).
 */
export async function fetchPilotParticipantCount(departmentIds: string[]): Promise<number> {
  let total = 0;
  for (const departmentId of departmentIds) {
    try {
      const members = (await apiGet('/departments/{departmentId}/users', {
        params: { departmentId },
      })) as unknown as DepartmentMember[];
      total += members.length;
    } catch {
      continue;
    }
  }
  return total;
}
